<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import type { AuctionCarPagination } from '@autobid/ui/types/Car'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useCarPageInfo } from '@autobid/ui/composables/car/useCarPageInfo'
import { useCarPaginationLinks } from '@autobid/ui/composables/car/useCarPaginationLinks'
import { useGetCarLink } from '@autobid/ui/composables/car/useGetCarLink'
import { usePrefetchPagination } from '@autobid/ui/composables/car/usePrefetchPagination'
import { paginationCva } from '@autobid/ui/utils/paginationCva'
import { FormKit, FormKitMessages } from '@formkit/vue'
import { useMutation } from '@tanstack/vue-query'
import { computed, ref } from 'vue'

interface Props {
  auctionCarPagination: AuctionCarPagination
  catalogNumber: number
  auctionId: number
}
const props = defineProps<Props>()

const { pageType } = useCarPageInfo()
const { $trpc } = useNuxtApp()
const { locale, t } = useI18n()
async function mutationFn(catalogNumber: number) {
  return await $trpc.auctions.getCarSlugByCatalogNumber.query({ lang: locale.value, catalogNumber, auctionId: props.auctionId })
}

const push = usePush()
const { mutateAsync: mutate } = useMutation({
  mutationFn,
  onSuccess: (result) => {
    if (!result) {
      push.error(t('shared-car.car-not-found'))
    }
  },
  onError: (e) => {
    const { message, handled } = getError(e)

    if (!handled) {
      push.error(message)
    }
  },
})

const { getLink } = useGetCarLink()
const router = useRouter()
const inputRef = ref()
async function handleSubmit() {
  const inputValue = Number(inputRef.value.node.value)
  if (inputValue === props.catalogNumber) {
    return
  }

  const result = await mutate(inputValue)
  const link = getLink(
    result,
    pageType.value === 'details' ? 'details' : 'barometer',
  )
  if (!link) {
    return
  }
  router.push(link)
}

const { pagination, prevLink, nextLink } = useCarPaginationLinks(computed(() => props.auctionCarPagination))

const { prefetch } = usePrefetchPagination(pagination)
</script>

<template>
  <div v-if="pagination" class="flex items-center justify-center gap-2">
    
<FormKitLazyProvider config-file="true">
<CommonLink
      :href="prevLink"
      :aria-label="$t('pagination.prev-page')"
      :class="paginationCva({ type: 'previous', disabled: !prevLink })"
      @mouseenter="prefetch('prev')"
    >
      <Icon mode="svg" name="mingcute:left-line" class="z-10 text-2xl" />
    </CommonLink>

    <div class="flex max-w-[10rem] items-center">
      <div class="relative">
        <FormKit type="form" :actions="false" @submit="handleSubmit">
          <label class="sr-only" for="currentPage">
            {{ $t('current-page') }}
          </label>
          <div class="-mt-6">
            <FormKit
              id="currentPage"
              ref="inputRef"
              type="number"
              :value="String(catalogNumber)"
              name="Page"
              :min="1"
              :max="pagination.lastCatalogNumber"
              :validation="`number|min:0|bidValidation|max:${pagination.lastCatalogNumber}`"
              validation-visibility="live"
              :disabled="!pagination || pagination?.total === 1"
              @blur="handleSubmit"
            />
            <FormKitMessages
              class="absolute -bottom-6 whitespace-nowrap"
              :node="inputRef?.node"
            />
          </div>
          <button type="submit" class="sr-only">
            {{ $t('submit') }}
          </button>
        </FormKit>
      </div>
      <div class="flex items-center gap-2 pl-3 pr-1 font-sans text-[#343434]">
        <span>{{ $t('from') }}</span>
        <span>{{ pagination.lastCatalogNumber }}</span>
      </div>
    </div>

    <CommonLink
      :href="nextLink"
      :aria-label="$t('pagination.next-page')"
      :class="paginationCva({ type: 'next', disabled: !nextLink })"
      @mouseenter="prefetch('next')"
    >
      <Icon mode="svg" name="mingcute:right-line" class="z-10 text-2xl" />
    </CommonLink>
</FormKitLazyProvider>

  </div>
</template>
